// The draft version form (DC-03): cut a draft (`POST /v1/operator/agent-templates/{id}/versions`,
// TemplateVersionDraft) or edit one (`PATCH /v1/operator/template-versions/{id}`,
// TemplateVersionPatch; the evals and rules sent replace the set). Structured rows for the
// role-to-model map, the evals and the rule → enforcer table, checked here the way the contract
// checks them (UUIDs, a written rule's reason) so the author sees a field, not a 422.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  NativeSelect,
  NativeSelectOption,
  Textarea,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import type {
  AgentTemplateVersion,
  TemplateEval,
  TemplateRule,
  TemplateVersionDetail,
} from "../../../api/types";
import { problemLines } from "./template-parts";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ROLE = /^[a-z][a-z0-9_]*$/;

type Paths = "gateway" | "subscription" | "subscription,gateway";
type RoleRow = { id: number; role: string; model: string; paths: Paths };
type EvalRow = {
  id: number;
  key: string;
  kind: TemplateEval["kind"];
  spec_ref: string;
  company_id: string;
  run_id: string;
  run_decision_id: string;
};
type RuleRow = {
  id: number;
  key: string;
  rule_text: string;
  enforcer_kind: TemplateRule["enforcer_kind"];
  enforcer_ref: string;
  company_id: string;
  run_id: string;
  run_decision_id: string;
  reason_if_written: string;
};
type FormState = {
  changelog: string;
  standing_orders_text: string;
  skill_index_ref: string;
  skill_index_token_cap: string;
  verification_skill_pack_id: string;
  supersedes_version_id: string;
  roles: RoleRow[];
  evals: EvalRow[];
  rules: RuleRow[];
};

const EVAL_KINDS: { value: TemplateEval["kind"]; label: string }[] = [
  { value: "fixture", label: "Fixture" },
  { value: "replay", label: "Replay" },
  { value: "live", label: "Live" },
];
const ENFORCERS: { value: TemplateRule["enforcer_kind"]; label: string }[] = [
  { value: "pipeline", label: "Pipeline step" },
  { value: "schema", label: "Schema" },
  { value: "tool_block", label: "Tool block" },
  { value: "eval", label: "Eval" },
  { value: "written_rule", label: "Written rule only" },
];
const PATHS: { value: Paths; label: string }[] = [
  { value: "gateway", label: "Gateway" },
  { value: "subscription", label: "Subscription" },
  { value: "subscription,gateway", label: "Subscription, then gateway" },
];

let rowSeq = 0;
const nextId = () => ++rowSeq;
const blankEval = (): EvalRow => ({
  id: nextId(),
  key: "",
  kind: "fixture",
  spec_ref: "",
  company_id: "",
  run_id: "",
  run_decision_id: "",
});
const blankRule = (): RuleRow => ({
  id: nextId(),
  key: "",
  rule_text: "",
  enforcer_kind: "pipeline",
  enforcer_ref: "",
  company_id: "",
  run_id: "",
  run_decision_id: "",
  reason_if_written: "",
});

/** The form: a draft's own content when editing, else empty with one `lead` role row. */
function initial(from: TemplateVersionDetail | null, supersedes: string | null): FormState {
  const v = from?.version;
  const roles: RoleRow[] = v
    ? Object.entries(v.role_model_map).map(([role, e]) => ({
        id: nextId(),
        role,
        model: e.model,
        paths: e.paths.join(",") as Paths,
      }))
    : [];
  return {
    changelog: "",
    standing_orders_text: v?.standing_orders_text ?? "",
    skill_index_ref: v?.skill_index_ref ?? "",
    skill_index_token_cap: String(v?.skill_index_token_cap ?? 2000),
    verification_skill_pack_id: v?.verification_skill_pack_id ?? "",
    supersedes_version_id: supersedes ?? "",
    roles: roles.length ? roles : [{ id: nextId(), role: "lead", model: "", paths: "gateway" }],
    evals: (from?.evals ?? []).map((e) => ({
      id: nextId(),
      key: e.key,
      kind: e.kind,
      spec_ref: e.spec_ref,
      company_id: e.proves_mistake_ref?.company_id ?? "",
      run_id: e.proves_mistake_ref?.run_id ?? "",
      run_decision_id: e.proves_mistake_ref?.run_decision_id ?? "",
    })),
    rules: (from?.rules ?? []).map((r) => ({
      id: nextId(),
      key: r.key,
      rule_text: r.rule_text,
      enforcer_kind: r.enforcer_kind,
      enforcer_ref: r.enforcer_ref,
      company_id: r.proven_against_ref.company_id,
      run_id: r.proven_against_ref.run_id,
      run_decision_id: r.proven_against_ref.run_decision_id ?? "",
      reason_if_written: r.reason_if_written ?? "",
    })),
  };
}

/** The problems the contract would answer 422 for, as readable lines (empty: send it). */
function problemsOf(f: FormState): string[] {
  const out: string[] = [];
  if (!f.standing_orders_text.trim()) out.push("Standing orders are required.");
  const cap = Number(f.skill_index_token_cap);
  if (!Number.isInteger(cap) || cap < 1 || cap > 32_000)
    out.push("Token cap: a whole number from 1 to 32000.");
  if (f.verification_skill_pack_id.trim() && !UUID.test(f.verification_skill_pack_id.trim()))
    out.push("Verification skill pack: must be a UUID.");
  const seen = new Set<string>();
  f.roles.forEach((r, i) => {
    if (!ROLE.test(r.role.trim()))
      out.push(`Role ${i + 1}: the name is lowercase letters, digits and _ (e.g. lead).`);
    else if (seen.has(r.role.trim())) out.push(`Role ${i + 1}: ${r.role} is listed twice.`);
    seen.add(r.role.trim());
    if (!r.model.trim()) out.push(`Role ${i + 1}: a model is required.`);
  });
  f.evals.forEach((e, i) => {
    if (!e.key.trim()) out.push(`Eval ${i + 1}: a key is required.`);
    if (!e.spec_ref.trim()) out.push(`Eval ${i + 1}: a spec ref is required.`);
    const any = e.company_id.trim() || e.run_id.trim() || e.run_decision_id.trim();
    if (any && !(UUID.test(e.company_id.trim()) && UUID.test(e.run_id.trim())))
      out.push(`Eval ${i + 1}: the mistake it proves needs a company and a run UUID.`);
    if (e.run_decision_id.trim() && !UUID.test(e.run_decision_id.trim()))
      out.push(`Eval ${i + 1}: the decision must be a UUID.`);
  });
  f.rules.forEach((r, i) => {
    if (!r.key.trim()) out.push(`Rule ${i + 1}: a key is required.`);
    if (!r.rule_text.trim()) out.push(`Rule ${i + 1}: the rule text is required.`);
    if (!r.enforcer_ref.trim()) out.push(`Rule ${i + 1}: an enforcer ref is required.`);
    if (!UUID.test(r.company_id.trim()) || !UUID.test(r.run_id.trim()))
      out.push(`Rule ${i + 1}: proven against needs a company and a run UUID.`);
    if (r.run_decision_id.trim() && !UUID.test(r.run_decision_id.trim()))
      out.push(`Rule ${i + 1}: the decision must be a UUID.`);
    if (r.enforcer_kind === "written_rule" && !r.reason_if_written.trim())
      out.push(`Rule ${i + 1}: a written rule needs the reason nothing stronger applies.`);
  });
  return out;
}

function mistake(company: string, run: string, decision: string) {
  return {
    company_id: company.trim(),
    run_id: run.trim(),
    ...(decision.trim() ? { run_decision_id: decision.trim() } : {}),
  };
}

/** The shared body of a draft and a patch (evals and rules always sent: the set is replaced). */
function bodyOf(f: FormState) {
  return {
    role_model_map: Object.fromEntries(
      f.roles.map((r) => [
        r.role.trim(),
        { model: r.model.trim(), paths: r.paths.split(",") as ("gateway" | "subscription")[] },
      ]),
    ),
    standing_orders_text: f.standing_orders_text.trim(),
    skill_index_token_cap: Number(f.skill_index_token_cap),
    evals: f.evals.map((e) => ({
      key: e.key.trim(),
      kind: e.kind,
      spec_ref: e.spec_ref.trim(),
      ...(e.company_id.trim()
        ? { proves_mistake_ref: mistake(e.company_id, e.run_id, e.run_decision_id) }
        : {}),
    })),
    rules: f.rules.map((r) => ({
      key: r.key.trim(),
      rule_text: r.rule_text.trim(),
      enforcer_kind: r.enforcer_kind,
      enforcer_ref: r.enforcer_ref.trim(),
      proven_against_ref: mistake(r.company_id, r.run_id, r.run_decision_id),
      ...(r.reason_if_written.trim() ? { reason_if_written: r.reason_if_written.trim() } : {}),
    })),
  };
}

export type VersionFormProps = {
  api: Api;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (detail: TemplateVersionDetail) => void;
} & (
  | {
      mode: "create";
      templateId: string;
      /** The template's versions, newest first (the "supersedes" choice). */
      versions: AgentTemplateVersion[];
      /** The preselected superseded version (the template's current one), if any. */
      supersedes: string | null;
    }
  | { mode: "edit"; detail: TemplateVersionDetail }
);

export function VersionFormDialog(props: VersionFormProps) {
  const { open, onOpenChange } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        {open ? <VersionForm {...props} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function VersionForm(props: VersionFormProps) {
  const { api, onSaved } = props;
  const [f, setF] = useState<FormState>(() =>
    props.mode === "edit"
      ? { ...initial(props.detail, null), changelog: props.detail.version.changelog ?? "" }
      : initial(null, props.supersedes),
  );
  const [checked, setChecked] = useState(false);
  const ids = {
    changelog: useId(),
    orders: useId(),
    index: useId(),
    cap: useId(),
    pack: useId(),
    supersedes: useId(),
  };
  const set = (patch: Partial<FormState>) => setF((s) => ({ ...s, ...patch }));
  const setRow = <K extends "roles" | "evals" | "rules">(
    list: K,
    id: number,
    patch: Partial<FormState[K][number]>,
  ) =>
    setF((s) => ({
      ...s,
      [list]: (s[list] as { id: number }[]).map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  const removeRow = (list: "roles" | "evals" | "rules", id: number) =>
    setF((s) => ({ ...s, [list]: (s[list] as { id: number }[]).filter((r) => r.id !== id) }));

  const save = useMutation({
    mutationFn: async (): Promise<TemplateVersionDetail> => {
      const body = bodyOf(f);
      if (props.mode === "create") {
        return api.client.operator.templateVersions.create({
          params: { template_id: props.templateId },
          idempotencyKey: newIdempotencyKey(),
          body: {
            ...body,
            ...(f.skill_index_ref.trim() ? { skill_index_ref: f.skill_index_ref.trim() } : {}),
            ...(f.verification_skill_pack_id.trim()
              ? { verification_skill_pack_id: f.verification_skill_pack_id.trim() }
              : {}),
            ...(f.supersedes_version_id ? { supersedes_version_id: f.supersedes_version_id } : {}),
            ...(f.changelog.trim() ? { changelog: f.changelog.trim() } : {}),
          },
        });
      }
      return api.client.operator.templateVersions.update({
        params: { version_id: props.detail.version.id },
        body: {
          ...body,
          skill_index_ref: f.skill_index_ref.trim() || null,
          verification_skill_pack_id: f.verification_skill_pack_id.trim() || null,
          changelog: f.changelog.trim() || null,
        },
      });
    },
    onSuccess: onSaved,
  });

  const problems = problemsOf(f);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setChecked(true);
    if (problems.length === 0) save.mutate();
  };
  const title =
    props.mode === "create" ? "New draft version" : `Edit draft v${props.detail.version.version}`;

  return (
    <form onSubmit={submit} className="grid gap-5" noValidate>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          A draft stays editable until it validates. Validation checks the skill index against its
          token cap, the verification skill, the lead model and every eval.
        </DialogDescription>
      </DialogHeader>
      <FieldGroup>
        {props.mode === "create" ? (
          <Field>
            <FieldLabel htmlFor={ids.supersedes}>Supersedes</FieldLabel>
            <NativeSelect
              id={ids.supersedes}
              value={f.supersedes_version_id}
              onChange={(e) => set({ supersedes_version_id: e.target.value })}
            >
              <NativeSelectOption value="">Nothing (a fresh start)</NativeSelectOption>
              {props.versions.map((v) => (
                <NativeSelectOption key={v.id} value={v.id}>
                  v{v.version} · {v.state}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        ) : null}
        <Field>
          <FieldLabel htmlFor={ids.changelog}>Changelog</FieldLabel>
          <Input
            id={ids.changelog}
            maxLength={2000}
            value={f.changelog}
            onChange={(e) => set({ changelog: e.target.value })}
          />
          <FieldDescription>What changed and why, in a sentence.</FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.orders}>Standing orders</FieldLabel>
          <Textarea
            id={ids.orders}
            rows={6}
            maxLength={10_000}
            value={f.standing_orders_text}
            onChange={(e) => set({ standing_orders_text: e.target.value })}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor={ids.index}>Skill index</FieldLabel>
            <Input
              id={ids.index}
              placeholder="templates/…/skill-index.json"
              maxLength={512}
              value={f.skill_index_ref}
              onChange={(e) => set({ skill_index_ref: e.target.value })}
            />
            <FieldDescription>The stored skill index's object key.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.cap}>Token cap</FieldLabel>
            <Input
              id={ids.cap}
              type="number"
              min={1}
              max={32000}
              value={f.skill_index_token_cap}
              onChange={(e) => set({ skill_index_token_cap: e.target.value })}
            />
          </Field>
        </div>
        <Field>
          <FieldLabel htmlFor={ids.pack}>Verification skill pack</FieldLabel>
          <Input
            id={ids.pack}
            placeholder="UUID"
            value={f.verification_skill_pack_id}
            onChange={(e) => set({ verification_skill_pack_id: e.target.value })}
          />
          <FieldDescription>
            Required to publish: the skill that verifies the work.
          </FieldDescription>
        </Field>

        <FieldSet data-slot="role-rows">
          <FieldLegend>Role-to-model map</FieldLegend>
          {f.roles.map((r, i) => (
            <div key={r.id} className="grid items-end gap-2 sm:grid-cols-[1fr_1.5fr_1.5fr_auto]">
              <Input
                aria-label={`Role ${i + 1} name`}
                placeholder="lead"
                value={r.role}
                onChange={(e) => setRow("roles", r.id, { role: e.target.value })}
              />
              <Input
                aria-label={`Role ${i + 1} model`}
                placeholder="gpt-4o"
                value={r.model}
                onChange={(e) => setRow("roles", r.id, { model: e.target.value })}
              />
              <NativeSelect
                aria-label={`Role ${i + 1} paths`}
                value={r.paths}
                onChange={(e) => setRow("roles", r.id, { paths: e.target.value as Paths })}
              >
                {PATHS.map((p) => (
                  <NativeSelectOption key={p.value} value={p.value}>
                    {p.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove role ${i + 1}`}
                disabled={f.roles.length === 1}
                onClick={() => removeRow("roles", r.id)}
              >
                <Trash2Icon aria-hidden="true" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() =>
              set({ roles: [...f.roles, { id: nextId(), role: "", model: "", paths: "gateway" }] })
            }
          >
            <PlusIcon aria-hidden="true" />
            Add role
          </Button>
          <FieldDescription>
            The `lead` role's model measures the skill index against its token cap.
          </FieldDescription>
        </FieldSet>

        <FieldSet data-slot="eval-rows">
          <FieldLegend>Evals</FieldLegend>
          {f.evals.length === 0 ? (
            <FieldDescription>
              No evals yet. A version needs at least one to publish.
            </FieldDescription>
          ) : null}
          {f.evals.map((e, i) => (
            <div key={e.id} className="grid gap-2 rounded-md border border-border p-3">
              <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_2fr_auto]">
                <Input
                  aria-label={`Eval ${i + 1} key`}
                  placeholder="key"
                  value={e.key}
                  onChange={(ev) => setRow("evals", e.id, { key: ev.target.value })}
                />
                <NativeSelect
                  aria-label={`Eval ${i + 1} kind`}
                  value={e.kind}
                  onChange={(ev) =>
                    setRow("evals", e.id, { kind: ev.target.value as TemplateEval["kind"] })
                  }
                >
                  {EVAL_KINDS.map((k) => (
                    <NativeSelectOption key={k.value} value={k.value}>
                      {k.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <Input
                  aria-label={`Eval ${i + 1} spec ref`}
                  placeholder="templates/…/evals/….json"
                  value={e.spec_ref}
                  onChange={(ev) => setRow("evals", e.id, { spec_ref: ev.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove eval ${i + 1}`}
                  onClick={() => removeRow("evals", e.id)}
                >
                  <Trash2Icon aria-hidden="true" />
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <Input
                  aria-label={`Eval ${i + 1} mistake company`}
                  placeholder="Proves mistake: company UUID (optional)"
                  value={e.company_id}
                  onChange={(ev) => setRow("evals", e.id, { company_id: ev.target.value })}
                />
                <Input
                  aria-label={`Eval ${i + 1} mistake run`}
                  placeholder="Run UUID"
                  value={e.run_id}
                  onChange={(ev) => setRow("evals", e.id, { run_id: ev.target.value })}
                />
                <Input
                  aria-label={`Eval ${i + 1} mistake decision`}
                  placeholder="Decision UUID (optional)"
                  value={e.run_decision_id}
                  onChange={(ev) => setRow("evals", e.id, { run_decision_id: ev.target.value })}
                />
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => set({ evals: [...f.evals, blankEval()] })}
          >
            <PlusIcon aria-hidden="true" />
            Add eval
          </Button>
        </FieldSet>

        <FieldSet data-slot="rule-rows">
          <FieldLegend>Rules and their enforcers</FieldLegend>
          {f.rules.length === 0 ? (
            <FieldDescription>
              No rules yet. Each rule names what enforces it and the past mistake it is proven on.
            </FieldDescription>
          ) : null}
          {f.rules.map((r, i) => (
            <div key={r.id} className="grid gap-2 rounded-md border border-border p-3">
              <div className="grid items-end gap-2 sm:grid-cols-[1fr_3fr_auto]">
                <Input
                  aria-label={`Rule ${i + 1} key`}
                  placeholder="key"
                  value={r.key}
                  onChange={(ev) => setRow("rules", r.id, { key: ev.target.value })}
                />
                <Input
                  aria-label={`Rule ${i + 1} text`}
                  placeholder="The rule, in a sentence"
                  value={r.rule_text}
                  onChange={(ev) => setRow("rules", r.id, { rule_text: ev.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove rule ${i + 1}`}
                  onClick={() => removeRow("rules", r.id)}
                >
                  <Trash2Icon aria-hidden="true" />
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <NativeSelect
                  aria-label={`Rule ${i + 1} enforcer`}
                  value={r.enforcer_kind}
                  onChange={(ev) =>
                    setRow("rules", r.id, {
                      enforcer_kind: ev.target.value as TemplateRule["enforcer_kind"],
                    })
                  }
                >
                  {ENFORCERS.map((k) => (
                    <NativeSelectOption key={k.value} value={k.value}>
                      {k.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <Input
                  aria-label={`Rule ${i + 1} enforcer ref`}
                  placeholder="What enforces it (tool, schema, step)"
                  value={r.enforcer_ref}
                  onChange={(ev) => setRow("rules", r.id, { enforcer_ref: ev.target.value })}
                />
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <Input
                  aria-label={`Rule ${i + 1} proven company`}
                  placeholder="Proven against: company UUID"
                  value={r.company_id}
                  onChange={(ev) => setRow("rules", r.id, { company_id: ev.target.value })}
                />
                <Input
                  aria-label={`Rule ${i + 1} proven run`}
                  placeholder="Run UUID"
                  value={r.run_id}
                  onChange={(ev) => setRow("rules", r.id, { run_id: ev.target.value })}
                />
                <Input
                  aria-label={`Rule ${i + 1} proven decision`}
                  placeholder="Decision UUID (optional)"
                  value={r.run_decision_id}
                  onChange={(ev) => setRow("rules", r.id, { run_decision_id: ev.target.value })}
                />
              </div>
              {r.enforcer_kind === "written_rule" ? (
                <Input
                  aria-label={`Rule ${i + 1} reason if written`}
                  placeholder="Why nothing stronger than a written rule applies"
                  value={r.reason_if_written}
                  onChange={(ev) => setRow("rules", r.id, { reason_if_written: ev.target.value })}
                />
              ) : null}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => set({ rules: [...f.rules, blankRule()] })}
          >
            <PlusIcon aria-hidden="true" />
            Add rule
          </Button>
        </FieldSet>

        {checked && problems.length > 0 ? (
          <FieldError data-slot="form-problems">
            <ul className="list-disc pl-5">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </FieldError>
        ) : null}
        {save.isError ? (
          <FieldError data-slot="save-problems">
            {problemLines(save.error, "The version could not be saved.").join(" · ")}
          </FieldError>
        ) : null}
      </FieldGroup>
      <DialogFooter>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? "Saving…" : props.mode === "create" ? "Create draft" : "Save draft"}
        </Button>
      </DialogFooter>
    </form>
  );
}
